import { useBusinessTypes } from '@hooks/useBusinessTypes';
import { useCities } from '@hooks/useCities';
import { useConnectedAccount } from '@hooks/useConnectedAccount';
import { useLeads } from '@hooks/useLeads';
import { useLists } from '@hooks/useLists';
import type { MenuSelectItem } from '@components/MenuSelect';
import type {
  ApolloContactDoc,
  ApolloEventDoc,
  ApolloSequence,
  ContactInfoType,
  Lead,
  LeadStatusType,
  SequenceActionStatus,
  TechnologyType,
} from '@services/firebase/firestore';
import {
  addContactToSequence,
  addManualContact,
  addPersonToContacts,
  deleteLead,
  deleteManualContact,
  enrichContactPerson,
  getApolloContacts,
  getApolloEventsForContacts,
  getSequencesList,
  subscribeToApolloContact,
  subscribeToApolloContacts,
  triggerGifRegeneration,
  triggerLeadOnboarding,
  updateLeadBusinessType,
  updateLeadRating,
  updateLeadWhatsappNumber,
} from '@services/firebase/firestore';
import { getManageLeadsState, updateFilters, updateSelectedLead } from '@services/localStorage';
import { useAuthStore } from '@store/index';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDemoSiteUrl, getWhatsAppFromLead } from './utils';

export interface UseManageLeadsReturn {
  // Filters
  leadStatusFilter: LeadStatusType[];
  businessTypeFilter: string[];
  listFilter: string[];
  cityFilter: string[];
  technologyFilter: TechnologyType[];
  contactInfoFilter: ContactInfoType[];
  openFilter: string | null;
  businessTypeOptions: Array<{ value: string; label: string }>;
  /** Grouped options for the unified Types filter (Lists + Business Types). Values are namespaced `list:{id}` / `type:{slug}`. */
  typeOptions: MenuSelectItem[];
  /** Union of selected businessType (as `type:{slug}`) and list (as `list:{id}`) values for the Types SelectFilter. */
  typesFilter: string[];
  cityOptions: Array<{ value: string; label: string; subtitle: string }>;
  hasFilters: boolean;
  handleLeadStatusChange: (values: string[]) => void;
  handleBusinessTypeChange: (values: string[]) => void;
  /** Handles selection changes from the unified Types filter — splits namespaced values into businessType / list state. */
  handleTypesChange: (values: string[]) => void;
  handleCityChange: (values: string[]) => void;
  handleTechnologyChange: (values: string[]) => void;
  handleContactInfoChange: (values: string[]) => void;
  handleFilterOpenChange: (filterName: string) => (isOpen: boolean) => void;
  handleClearFilters: () => void;

  // Site list filter
  siteFilterText: string;
  setSiteFilterText: (text: string) => void;
  isFilterExpanded: boolean;
  setIsFilterExpanded: (expanded: boolean) => void;
  filterInputRef: React.RefObject<HTMLDivElement | null>;
  handleFilterBlur: () => void;
  handleFilterClear: () => void;
  handleFilterKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  // Leads data
  leads: Lead[];
  leadsLoading: boolean;
  displayedLeads: Lead[];

  // Selected lead
  selectedSiteId: string | null;
  setSelectedSiteId: (id: string | null) => void;
  selectedLead: Lead | null;
  selectedLeadInfo: {
    businessTypeName: string;
    cityName: string;
    sourceListId: string | null;
    sourceListName: string;
  };
  currentIndex: number;
  totalNavigableCount: number;
  handlePrevious: () => void;
  handleNext: () => void;

  // Lead status flags
  isSelectedLeadNotOk: boolean;
  leadRating: 'neutral' | 'ok' | 'not-ok';
  canDeleteLead: boolean;
  isSelectedLeadOnboarding: boolean;
  isSelectedLeadOnboarded: boolean;
  isOnboardDisabled: boolean;
  contactedCount: number;
  hasContactedAny: boolean;

  // GIF regeneration
  isGifRegenerating: boolean;
  gifCacheBustKey: number;
  handleRegenerateGif: () => Promise<void>;

  // Apollo / contacts data
  apolloContactsMap: Map<string, ApolloContactDoc>;
  apolloEventsMap: Map<string, ApolloEventDoc>;
  contactedPersonIds: Set<string>;
  sequences: ApolloSequence[];
  reachOutStates: Map<string, { status: SequenceActionStatus; message?: string }>;

  // Add-menu open state (shared by combined-empty and available-to-contact placements)
  isAddMenuOpen: boolean;
  setIsAddMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Action handlers
  handleLogout: () => Promise<void>;
  handleOnboardSite: () => Promise<void>;
  handleViewDemo: () => void;
  handleRatingChange: (rating: string) => Promise<void>;
  handleDeleteLead: () => Promise<void>;
  handleDeleteContact: () => Promise<void>;
  handleEnrichProfile: (personId: string) => Promise<void>;
  handleAddToContacts: (personId: string) => Promise<void>;
  handleReachOutWithSequence: (personId: string, sequenceId: string) => Promise<void>;
  handleReachOutWhatsApp: () => void;
  handleSaveWhatsappNumber: (whatsappNumber: string) => Promise<void>;

  // Modal state
  jsonModalPerson: { name: string; data: Record<string, unknown> } | null;
  setJsonModalPerson: (
    person: { name: string; data: Record<string, unknown> } | null
  ) => void;
  isAddContactModalOpen: boolean;
  setIsAddContactModalOpen: (open: boolean) => void;
  isWhatsappModalOpen: boolean;
  setIsWhatsappModalOpen: (open: boolean) => void;
  isDeleteLeadModalOpen: boolean;
  setIsDeleteLeadModalOpen: (open: boolean) => void;
  deleteContactTarget: { personId: string; name: string } | null;
  setDeleteContactTarget: (target: { personId: string; name: string } | null) => void;
  isChangeBusinessTypeModalOpen: boolean;
  setIsChangeBusinessTypeModalOpen: (open: boolean) => void;
  handleChangeBusinessType: (slug: string) => Promise<void>;

  // Direct addManualContact accessor (needed for AddContactModal onAdd)
  addManualContactForLead: (
    data: Parameters<typeof addManualContact>[1],
  ) => Promise<void>;
}

export const useManageLeads = (): UseManageLeadsReturn => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut } = useAuthStore();

  // Capture deep-link query params once on mount (survive URL cleanup)
  const [deepLinkParams] = useState(() => ({
    businessType: searchParams.get('businessType'),
    list: searchParams.get('list'),
    cityId: searchParams.get('cityId'),
    cityName: searchParams.get('cityName'),
  }));

  // Load saved state from localStorage
  const savedState = getManageLeadsState();

  // When arriving via deep-link, discard all saved filters so the user sees
  // a clean result set scoped only to the linked business type / city.
  const isDeepLink = Boolean(
    deepLinkParams.businessType ||
      deepLinkParams.list ||
      deepLinkParams.cityId ||
      deepLinkParams.cityName
  );

  // Filter states (deep-link clears saved state; query params override localStorage)
  const [leadStatusFilter, setLeadStatusFilter] = useState<LeadStatusType[]>(
    isDeepLink ? [] : (savedState.filters.leadStatus ?? [])
  );
  const [businessTypeFilter, setBusinessTypeFilter] = useState<string[]>(
    deepLinkParams.businessType
      ? [deepLinkParams.businessType]
      : (savedState.filters.businessType ?? [])
  );
  const [listFilter, setListFilter] = useState<string[]>(
    deepLinkParams.list ? [deepLinkParams.list] : (savedState.filters.list ?? [])
  );
  const [cityFilter, setCityFilter] = useState<string[]>(savedState.filters.city ?? []);
  const [technologyFilter, setTechnologyFilter] = useState<TechnologyType[]>(
    isDeepLink ? [] : (savedState.filters.technology ?? [])
  );
  const [contactInfoFilter, setContactInfoFilter] = useState<ContactInfoType[]>(
    isDeepLink ? [] : (savedState.filters.contactInfo ?? [])
  );

  // Open state for each filter dropdown
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  // Site list filter (initialized from localStorage, cleared on deep-link)
  const [siteFilterText, setSiteFilterText] = useState(
    isDeepLink ? '' : savedState.filters.siteFilterText
  );
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const filterBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const filterInputRef = useRef<HTMLDivElement | null>(null);

  // Selected site (cleared when deep-linking, otherwise from localStorage)
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(
    isDeepLink ? null : savedState.selected_lead
  );

  // Apollo contacts subcollection data for the selected lead (from Firestore)
  const [apolloContactsMap, setApolloContactsMap] = useState<Map<string, ApolloContactDoc>>(
    new Map()
  );

  // Apollo events data for contacted profiles (keyed by contactId)
  const [apolloEventsMap, setApolloEventsMap] = useState<Map<string, ApolloEventDoc>>(new Map());

  // Enrichment JSON modal state
  const [jsonModalPerson, setJsonModalPerson] = useState<{
    name: string;
    data: Record<string, unknown>;
  } | null>(null);

  // Add Contact modal state
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);

  // Add/Edit WhatsApp modal state
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);

  // Delete Lead modal state
  const [isDeleteLeadModalOpen, setIsDeleteLeadModalOpen] = useState(false);

  // Change Business Type modal state
  const [isChangeBusinessTypeModalOpen, setIsChangeBusinessTypeModalOpen] = useState(false);

  // Delete Contact modal state
  const [deleteContactTarget, setDeleteContactTarget] = useState<{
    personId: string;
    name: string;
  } | null>(null);

  // Add menu dropdown state (for "Add Contact" / "Add WhatsApp" options)
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  // Available Apollo sequences for reach-out popover
  const [sequences, setSequences] = useState<ApolloSequence[]>([]);

  // Track reach-out states per profile (key: `${leadId}-${personId}`)
  const [reachOutStates, setReachOutStates] = useState<
    Map<string, { status: SequenceActionStatus; message?: string }>
  >(new Map());

  // Store subscription unsubscribes for cleanup
  const apolloContactUnsubs = useRef<Map<string, () => void>>(new Map());

  // Fetch data from Firestore
  const { businessTypes, loading: businessTypesLoading } = useBusinessTypes();
  const { lists, loading: listsLoading } = useLists();
  const { cities, loading: citiesLoading } = useCities();
  const { onboardUid } = useConnectedAccount();

  const { leads, loading: leadsLoading } = useLeads({
    leadStatus: leadStatusFilter.length > 0 ? leadStatusFilter : undefined,
    businessType: businessTypeFilter.length > 0 ? businessTypeFilter : undefined,
    listId: listFilter.length > 0 ? listFilter : undefined,
    cityId: cityFilter.length > 0 ? cityFilter : undefined,
    technology: technologyFilter.length > 0 ? technologyFilter : undefined,
    contactInfo: contactInfoFilter.length > 0 ? contactInfoFilter : undefined,
    currentUserUid: leadStatusFilter.includes('onboarded-by-me')
      ? (onboardUid ?? undefined)
      : undefined,
  });

  // Resolve deep-link city param to city ID once cities are loaded
  useEffect(() => {
    if (
      (!deepLinkParams.cityId && !deepLinkParams.cityName) ||
      citiesLoading ||
      !cities ||
      cities.length === 0
    )
      return;
    const match =
      (deepLinkParams.cityId && cities.find((c) => c.id === deepLinkParams.cityId)) ||
      (deepLinkParams.cityName && cities.find((c) => c.name === deepLinkParams.cityName));
    if (match) {
      setCityFilter([match.id]);
    }
  }, [deepLinkParams.cityId, deepLinkParams.cityName, citiesLoading, cities]);

  // Clean up deep-link query params after they've been consumed
  useEffect(() => {
    if (isDeepLink) {
      navigate('/manage-leads', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    updateFilters({
      leadStatus: leadStatusFilter,
      businessType: businessTypeFilter,
      list: listFilter,
      city: cityFilter,
      technology: technologyFilter,
      contactInfo: contactInfoFilter,
      siteFilterText,
    });
  }, [
    leadStatusFilter,
    businessTypeFilter,
    listFilter,
    cityFilter,
    technologyFilter,
    contactInfoFilter,
    siteFilterText,
  ]);

  // Save selected lead to localStorage whenever it changes
  useEffect(() => {
    updateSelectedLead(selectedSiteId);
  }, [selectedSiteId]);

  // Fetch available Apollo sequences on mount
  useEffect(() => {
    const fetchSequences = async () => {
      try {
        const seqs = await getSequencesList();
        setSequences([...seqs].sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Failed to fetch sequences:', error);
      }
    };
    fetchSequences();
  }, []);

  // Cleanup apollo contact subscriptions on unmount
  useEffect(() => {
    const unsubs = apolloContactUnsubs.current;
    return () => {
      unsubs.forEach((unsub) => unsub());
      unsubs.clear();
    };
  }, []);

  // Subscribe to apollo_contacts subcollection for the selected lead
  useEffect(() => {
    if (!selectedSiteId) {
      setApolloContactsMap(new Map());
      setApolloEventsMap(new Map());
      return;
    }

    const unsubscribe = subscribeToApolloContacts(
      selectedSiteId,
      (contactsMap) => {
        setApolloContactsMap(contactsMap);
      },
      (error) => {
        console.error('Failed to subscribe to apollo contacts:', error);
        setApolloContactsMap(new Map());
      }
    );

    return () => {
      unsubscribe();
    };
  }, [selectedSiteId]);

  // Build filter options from fetched data
  const businessTypeOptions = useMemo(() => {
    if (businessTypesLoading || !businessTypes) return [];
    return businessTypes.map((bt) => ({ value: bt.slug, label: bt.name }));
  }, [businessTypes, businessTypesLoading]);

  // Grouped options for the unified Types filter (SC-2171). Values are
  // namespaced so the two sources can share one MenuSelect without collisions:
  //   `type:{slug}` for business types
  //   `list:{id}`   for Apollo saved lists (SC-2143)
  // Lists are rendered first so operators see curated lists above the
  // long alphabetical business-type catalogue.
  const typeOptions = useMemo<MenuSelectItem[]>(() => {
    const listItems: MenuSelectItem[] =
      listsLoading || !lists
        ? []
        : lists.map((l) => ({ value: `list:${l.id}`, label: l.name, group: 'Lists' }));
    const businessTypeItems: MenuSelectItem[] =
      businessTypesLoading || !businessTypes
        ? []
        : businessTypes.map((bt) => ({
            value: `type:${bt.slug}`,
            label: bt.name,
            group: 'Business Types',
          }));
    return [...listItems, ...businessTypeItems];
  }, [lists, listsLoading, businessTypes, businessTypesLoading]);

  const typesFilter = useMemo(
    () => [...listFilter.map((id) => `list:${id}`), ...businessTypeFilter.map((s) => `type:${s}`)],
    [listFilter, businessTypeFilter]
  );

  const cityOptions = useMemo(() => {
    if (citiesLoading || !cities) return [];
    return cities.map((city) => ({
      value: city.id,
      label: city.name,
      subtitle: (city.region || city.country) ?? '',
    }));
  }, [cities, citiesLoading]);

  // Check if any filter is selected
  const hasFilters = useMemo(() => {
    return (
      leadStatusFilter.length > 0 ||
      businessTypeFilter.length > 0 ||
      listFilter.length > 0 ||
      cityFilter.length > 0 ||
      technologyFilter.length > 0 ||
      contactInfoFilter.length > 0
    );
  }, [
    leadStatusFilter,
    businessTypeFilter,
    listFilter,
    cityFilter,
    technologyFilter,
    contactInfoFilter,
  ]);

  // Further filter by site name or URL search
  const displayedLeads = useMemo(() => {
    if (!siteFilterText) return leads;
    const searchText = siteFilterText.toLowerCase();
    return leads.filter(
      (lead) =>
        (lead.name && lead.name.toLowerCase().includes(searchText)) ||
        (lead.url && lead.url.toLowerCase().includes(searchText))
    );
  }, [leads, siteFilterText]);

  // Clean up filter blur timeout on unmount
  useEffect(() => () => clearTimeout(filterBlurTimeoutRef.current), []);

  // Focus the filter input when expanded
  useEffect(() => {
    if (isFilterExpanded) {
      const input = filterInputRef.current?.querySelector('input');
      input?.focus();
    }
  }, [isFilterExpanded]);

  const handleFilterBlur = useCallback(() => {
    filterBlurTimeoutRef.current = setTimeout(() => {
      setIsFilterExpanded(false);
    }, 150);
  }, []);

  const handleFilterClear = useCallback(() => {
    clearTimeout(filterBlurTimeoutRef.current);
    setSiteFilterText('');
    setIsFilterExpanded(false);
  }, []);

  const handleFilterKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsFilterExpanded(false);
    }
  }, []);

  // Get selected lead
  const selectedLead = useMemo(() => {
    return leads.find((lead) => lead.id === selectedSiteId) || null;
  }, [selectedSiteId, leads]);

  // Load existing apollo_contacts statuses when a lead is selected
  useEffect(() => {
    if (!selectedSiteId || !selectedLead) return;

    const personToContact = selectedLead.person_id_to_contact_id;
    if (!personToContact) return;

    const loadApolloContacts = async () => {
      const apolloDocs = await getApolloContacts(selectedSiteId);
      if (apolloDocs.size === 0) return;

      // Build reverse map: contactId → personId
      const contactToPersonId = new Map<string, string>();
      for (const [personId, contactId] of Object.entries(personToContact)) {
        if (contactId) {
          contactToPersonId.set(contactId, personId);
        }
      }

      const newReachOutEntries: Array<
        [string, { status: SequenceActionStatus; message?: string }]
      > = [];

      for (const [contactId, apolloDoc] of apolloDocs) {
        const action = apolloDoc.sequence_action;
        if (!action || action.status === 'pending') continue;

        const personId = contactToPersonId.get(contactId);
        if (!personId) continue;

        const stateKey = `${selectedSiteId}-${personId}`;
        newReachOutEntries.push([
          stateKey,
          { status: action.status, message: action.error_message || action.skipped_reason },
        ]);
      }

      if (newReachOutEntries.length > 0) {
        setReachOutStates((prev) => {
          const newMap = new Map(prev);
          for (const [key, value] of newReachOutEntries) {
            if (!newMap.has(key)) {
              newMap.set(key, value);
            }
          }
          return newMap;
        });
      }
    };

    loadApolloContacts();
  }, [selectedSiteId, selectedLead]);

  // Fetch apollo_events for all contacted profiles when lead changes
  useEffect(() => {
    if (!selectedSiteId || !selectedLead) {
      setApolloEventsMap(new Map());
      return;
    }

    const personToContact = selectedLead.person_id_to_contact_id;
    if (!personToContact) {
      setApolloEventsMap(new Map());
      return;
    }

    const contactIds = Object.values(personToContact).filter(
      (id): id is string => id !== null && id !== undefined
    );

    if (contactIds.length === 0) {
      setApolloEventsMap(new Map());
      return;
    }

    const fetchEvents = async () => {
      const eventsMap = await getApolloEventsForContacts(selectedSiteId, contactIds);
      setApolloEventsMap(eventsMap);
    };

    fetchEvents();
  }, [selectedSiteId, selectedLead]);

  // Resolve subtitle parts for the selected lead: business type, city, and
  // (SC-2189) Apollo list source. Business type and list are kept independent
  // — leads can have neither, either, or both. The consumer composes the
  // rendered subtitle from the parts.
  const selectedLeadInfo = useMemo(() => {
    if (!selectedLead) {
      return {
        businessTypeName: '',
        cityName: '',
        sourceListId: null as string | null,
        sourceListName: '',
      };
    }

    const businessTypeName =
      businessTypes.find((bt) => bt.slug === selectedLead.scope?.business_type)?.name || '';
    const cityName =
      cities.find((city) => city.id === selectedLead.scope?.cities?.id)?.name || '';

    // Defensive prefix match — future scope.source prefixes (gemini:, manual:, …)
    // fall through until they get explicit handling.
    const source = selectedLead.scope?.source;
    const sourceListId = source && source.startsWith('list:') ? source.slice(5) : null;
    // Falls back gracefully to '' if the list document has been deleted.
    const sourceListName = sourceListId
      ? (lists.find((l) => l.id === sourceListId)?.name ?? '')
      : '';

    return { businessTypeName, cityName, sourceListId, sourceListName };
  }, [selectedLead, businessTypes, cities, lists]);

  // Get current index for navigation (excluding notOK leads)
  const currentIndex = useMemo(() => {
    if (!selectedSiteId) return 0;
    const navigableLeads = displayedLeads.filter((lead) => !lead.notOK);
    const idx = navigableLeads.findIndex((lead) => lead.id === selectedSiteId);
    return idx >= 0 ? idx + 1 : 0;
  }, [selectedSiteId, displayedLeads]);

  // Get total count of navigable leads (excluding notOK leads)
  const totalNavigableCount = useMemo(() => {
    return displayedLeads.filter((lead) => !lead.notOK).length;
  }, [displayedLeads]);

  const handleFilterOpenChange = (filterName: string) => (isOpen: boolean) => {
    setOpenFilter(isOpen ? filterName : null);
  };

  // Handle filter changes — toggle behavior (clicking same filter again removes it)
  const handleLeadStatusChange = (values: string[]) => {
    setLeadStatusFilter(values as LeadStatusType[]);
    setSelectedSiteId(null);
  };

  const handleBusinessTypeChange = (values: string[]) => {
    setBusinessTypeFilter(values);
    setSelectedSiteId(null);
  };

  // Unified handler for the Types filter (SC-2171). Values arrive namespaced
  // (`list:{id}` / `type:{slug}`); split them back into their two state slices.
  const handleTypesChange = (values: string[]) => {
    const nextListIds: string[] = [];
    const nextBusinessTypes: string[] = [];
    for (const v of values) {
      if (v.startsWith('list:')) nextListIds.push(v.slice(5));
      else if (v.startsWith('type:')) nextBusinessTypes.push(v.slice(5));
    }
    setListFilter(nextListIds);
    setBusinessTypeFilter(nextBusinessTypes);
    setSelectedSiteId(null);
  };

  const handleCityChange = (values: string[]) => {
    setCityFilter(values);
    setSelectedSiteId(null);
  };

  const handleTechnologyChange = (values: string[]) => {
    setTechnologyFilter(values as TechnologyType[]);
    setSelectedSiteId(null);
  };

  const handleContactInfoChange = (values: string[]) => {
    setContactInfoFilter(values as ContactInfoType[]);
    setSelectedSiteId(null);
  };

  const handleClearFilters = () => {
    setLeadStatusFilter([]);
    setBusinessTypeFilter([]);
    setListFilter([]);
    setCityFilter([]);
    setTechnologyFilter([]);
    setContactInfoFilter([]);
    setSelectedSiteId(null);
    setSiteFilterText('');
  };

  const handlePrevious = () => {
    const currentIdx = displayedLeads.findIndex((lead) => lead.id === selectedSiteId);
    if (currentIdx > 0) {
      for (let i = currentIdx - 1; i >= 0; i--) {
        const prevLead = displayedLeads[i];
        if (prevLead && !prevLead.notOK) {
          setSelectedSiteId(prevLead.id);
          return;
        }
      }
    }
  };

  const handleNext = () => {
    const currentIdx = displayedLeads.findIndex((lead) => lead.id === selectedSiteId);
    if (currentIdx < displayedLeads.length - 1) {
      for (let i = currentIdx + 1; i < displayedLeads.length; i++) {
        const nextLead = displayedLeads[i];
        if (nextLead && !nextLead.notOK) {
          setSelectedSiteId(nextLead.id);
          return;
        }
      }
    }
  };

  // Check if selected lead is marked as "not ok"
  const isSelectedLeadNotOk = useMemo(() => {
    return selectedLead?.notOK === true;
  }, [selectedLead]);

  // Derive the 3-state lead rating
  const leadRating = useMemo((): 'neutral' | 'ok' | 'not-ok' => {
    if (!selectedLead) return 'neutral';
    if (selectedLead.notOK) return 'not-ok';
    if (selectedLead.markedOK) return 'ok';
    return 'neutral';
  }, [selectedLead]);

  // Visible when: no contacts have been enriched OR lead is marked as Not OK
  const canDeleteLead = useMemo(() => {
    if (!selectedLead) return false;
    const hasEnrichedContacts =
      (selectedLead.contacts?.apollo?.people_enriched?.length ?? 0) > 0;
    return !hasEnrichedContacts || isSelectedLeadNotOk;
  }, [selectedLead, isSelectedLeadNotOk]);

  // Derive set of contacted person IDs from apollo_contacts subcollection
  const contactedPersonIds = useMemo(() => {
    const contactedSet = new Set<string>();
    if (!selectedLead) return contactedSet;

    const personToContactMap = selectedLead.person_id_to_contact_id;
    if (!personToContactMap) return contactedSet;

    for (const [personId, contactId] of Object.entries(personToContactMap)) {
      if (!contactId) continue;
      const apolloDoc = apolloContactsMap.get(contactId);
      if (apolloDoc?.sequence_action?.status === 'completed') {
        contactedSet.add(personId);
      }
    }

    return contactedSet;
  }, [selectedLead, apolloContactsMap]);

  const contactedCount = contactedPersonIds.size;
  const hasContactedAny = contactedCount > 0;

  // Check if selected lead is currently onboarding (has onboard_uid in lead doc)
  const isSelectedLeadOnboarding = useMemo(() => {
    return !!selectedLead?.onboard_uid;
  }, [selectedLead]);

  // Check if selected lead is onboarded (has site_id)
  const isSelectedLeadOnboarded = useMemo(() => {
    return !!selectedLead?.site_id;
  }, [selectedLead]);

  // GIF regeneration tracking
  const [gifCacheBustKey, setGifCacheBustKey] = useState<number>(0);
  const prevRegenerateRef = useRef<boolean | undefined>(undefined);

  const isGifRegenerating = useMemo(() => {
    return !!selectedLead?.demo_gif?.regenerate;
  }, [selectedLead?.demo_gif?.regenerate]);

  // Watch for regeneration completion: regenerate was true, now false
  useEffect(() => {
    const currentRegenerate = selectedLead?.demo_gif?.regenerate;
    if (prevRegenerateRef.current === true && !currentRegenerate) {
      setGifCacheBustKey(Date.now());
    }
    prevRegenerateRef.current = currentRegenerate;
  }, [selectedLead?.demo_gif?.regenerate]);

  // Reset cache bust key when selected lead changes
  useEffect(() => {
    setGifCacheBustKey(0);
    prevRegenerateRef.current = undefined;
  }, [selectedSiteId]);

  const handleRegenerateGif = useCallback(async () => {
    if (!selectedSiteId || isGifRegenerating) return;
    try {
      await triggerGifRegeneration(selectedSiteId);
    } catch (error) {
      console.error('Failed to trigger GIF regeneration:', error);
    }
  }, [selectedSiteId, isGifRegenerating]);

  // Check if onboard button should be disabled
  const isOnboardDisabled = useMemo(() => {
    if (!onboardUid) return true;
    if (isSelectedLeadNotOk) return true;
    if (isSelectedLeadOnboarding) return true;
    return false;
  }, [onboardUid, isSelectedLeadNotOk, isSelectedLeadOnboarding]);

  // ---------------------------------------------------------------------------
  // Action handlers
  // ---------------------------------------------------------------------------

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleOnboardSite = async () => {
    if (!selectedSiteId || !onboardUid) {
      console.error('Cannot onboard: missing lead ID or onboard_uid');
      return;
    }
    try {
      await triggerLeadOnboarding(selectedSiteId, onboardUid);
    } catch (error) {
      console.error('Failed to trigger onboarding:', error);
    }
  };

  const handleRatingChange = async (rating: string) => {
    if (!selectedSiteId) return;
    try {
      await updateLeadRating(selectedSiteId, rating as 'ok' | 'not-ok' | 'neutral');

      // Auto-navigate to next available lead when marking as not-ok
      if (rating === 'not-ok') {
        const currentIdx = displayedLeads.findIndex((lead) => lead.id === selectedSiteId);

        for (let i = currentIdx + 1; i < displayedLeads.length; i++) {
          const nextLead = displayedLeads[i];
          if (nextLead && !nextLead.notOK) {
            setSelectedSiteId(nextLead.id);
            return;
          }
        }
        for (let i = currentIdx - 1; i >= 0; i--) {
          const prevLead = displayedLeads[i];
          if (prevLead && !prevLead.notOK) {
            setSelectedSiteId(prevLead.id);
            return;
          }
        }

        setSelectedSiteId(null);
      }
    } catch (error) {
      console.error('Failed to update lead rating:', error);
    }
  };

  const handleDeleteLead = async () => {
    if (!selectedSiteId) return;

    // Capture navigation target BEFORE deletion
    const currentIdx = displayedLeads.findIndex((lead) => lead.id === selectedSiteId);
    let nextSelectedId: string | null = null;

    for (let i = currentIdx + 1; i < displayedLeads.length; i++) {
      const nextLead = displayedLeads[i];
      if (nextLead && !nextLead.notOK) {
        nextSelectedId = nextLead.id;
        break;
      }
    }
    if (!nextSelectedId) {
      for (let i = currentIdx - 1; i >= 0; i--) {
        const prevLead = displayedLeads[i];
        if (prevLead && !prevLead.notOK) {
          nextSelectedId = prevLead.id;
          break;
        }
      }
    }

    await deleteLead(selectedSiteId);
    setSelectedSiteId(nextSelectedId);
  };

  const handleDeleteContact = async () => {
    if (!selectedSiteId || !deleteContactTarget) return;
    await deleteManualContact(selectedSiteId, deleteContactTarget.personId);
    setDeleteContactTarget(null);
  };

  const handleChangeBusinessType = async (slug: string) => {
    if (!selectedSiteId) return;
    await updateLeadBusinessType(selectedSiteId, slug);
  };

  const handleEnrichProfile = async (personId: string) => {
    if (!selectedSiteId) return;
    try {
      await enrichContactPerson(selectedSiteId, personId);
    } catch (error) {
      console.error('Failed to enrich profile:', error);
    }
  };

  const handleAddToContacts = async (personId: string) => {
    if (!selectedSiteId) return;
    try {
      await addPersonToContacts(selectedSiteId, personId);
    } catch (error) {
      console.error('Failed to add person to contacts:', error);
    }
  };

  const handleReachOutWithSequence = useCallback(
    async (personId: string, sequenceId: string) => {
      if (!selectedSiteId || !selectedLead) return;

      const contactId = selectedLead.person_id_to_contact_id?.[personId];
      if (!contactId) {
        console.error('No contact ID found for person:', personId);
        return;
      }

      const stateKey = `${selectedSiteId}-${personId}`;

      // Set pending state immediately
      setReachOutStates((prev) => {
        const newMap = new Map(prev);
        newMap.set(stateKey, { status: 'pending' });
        return newMap;
      });

      try {
        await addContactToSequence(selectedSiteId, contactId, sequenceId);

        const unsubscribe = subscribeToApolloContact(
          selectedSiteId,
          contactId,
          (apolloDoc) => {
            const action = apolloDoc?.sequence_action;
            if (!action || action.status === 'pending') return;

            setReachOutStates((prev) => {
              const newMap = new Map(prev);
              newMap.set(stateKey, {
                status: action.status,
                message: action.error_message || action.skipped_reason,
              });
              return newMap;
            });

            unsubscribe();
            apolloContactUnsubs.current.delete(stateKey);
          },
          (error) => {
            console.error('Error watching apollo contact status:', error);
            setReachOutStates((prev) => {
              const newMap = new Map(prev);
              newMap.set(stateKey, { status: 'failed', message: error.message });
              return newMap;
            });
            apolloContactUnsubs.current.delete(stateKey);
          }
        );

        apolloContactUnsubs.current.set(stateKey, unsubscribe);
      } catch (error) {
        console.error('Failed to add contact to sequence:', error);
        setReachOutStates((prev) => {
          const newMap = new Map(prev);
          newMap.set(stateKey, {
            status: 'failed',
            message: error instanceof Error ? error.message : 'Unknown error',
          });
          return newMap;
        });
      }
    },
    [selectedSiteId, selectedLead]
  );

  const handleReachOutWhatsApp = () => {
    if (!selectedSiteId || !selectedLead) return;
    const whatsApp = getWhatsAppFromLead(selectedLead);
    if (whatsApp) {
      window.open(whatsApp.href, '_blank');
    }
  };

  const handleSaveWhatsappNumber = async (whatsappNumber: string) => {
    if (!selectedSiteId) return;
    try {
      await updateLeadWhatsappNumber(selectedSiteId, whatsappNumber);
    } catch (error) {
      console.error('Failed to update WhatsApp number:', error);
    }
  };

  const handleViewDemo = () => {
    if (!selectedLead?.site_id) {
      console.error('No demo site ID found for lead:', selectedLead?.name);
      return;
    }
    window.open(getDemoSiteUrl(selectedLead.site_id), '_blank');
  };

  const addManualContactForLead = async (data: Parameters<typeof addManualContact>[1]) => {
    if (!selectedSiteId) return;
    await addManualContact(selectedSiteId, data, selectedLead?.name);
  };

  return {
    // Filters
    leadStatusFilter,
    businessTypeFilter,
    listFilter,
    cityFilter,
    technologyFilter,
    contactInfoFilter,
    openFilter,
    businessTypeOptions,
    typeOptions,
    typesFilter,
    cityOptions,
    hasFilters,
    handleLeadStatusChange,
    handleBusinessTypeChange,
    handleTypesChange,
    handleCityChange,
    handleTechnologyChange,
    handleContactInfoChange,
    handleFilterOpenChange,
    handleClearFilters,

    // Site list filter
    siteFilterText,
    setSiteFilterText,
    isFilterExpanded,
    setIsFilterExpanded,
    filterInputRef,
    handleFilterBlur,
    handleFilterClear,
    handleFilterKeyDown,

    // Leads data
    leads,
    leadsLoading,
    displayedLeads,

    // Selected lead
    selectedSiteId,
    setSelectedSiteId,
    selectedLead,
    selectedLeadInfo,
    currentIndex,
    totalNavigableCount,
    handlePrevious,
    handleNext,

    // Lead status flags
    isSelectedLeadNotOk,
    leadRating,
    canDeleteLead,
    isSelectedLeadOnboarding,
    isSelectedLeadOnboarded,
    isOnboardDisabled,
    contactedCount,
    hasContactedAny,

    // GIF
    isGifRegenerating,
    gifCacheBustKey,
    handleRegenerateGif,

    // Apollo data
    apolloContactsMap,
    apolloEventsMap,
    contactedPersonIds,
    sequences,
    reachOutStates,

    // Add menu
    isAddMenuOpen,
    setIsAddMenuOpen,

    // Action handlers
    handleLogout,
    handleOnboardSite,
    handleViewDemo,
    handleRatingChange,
    handleDeleteLead,
    handleDeleteContact,
    handleEnrichProfile,
    handleAddToContacts,
    handleReachOutWithSequence,
    handleReachOutWhatsApp,
    handleSaveWhatsappNumber,

    // Modal state
    jsonModalPerson,
    setJsonModalPerson,
    isAddContactModalOpen,
    setIsAddContactModalOpen,
    isWhatsappModalOpen,
    setIsWhatsappModalOpen,
    isDeleteLeadModalOpen,
    setIsDeleteLeadModalOpen,
    deleteContactTarget,
    setDeleteContactTarget,
    isChangeBusinessTypeModalOpen,
    setIsChangeBusinessTypeModalOpen,
    handleChangeBusinessType,

    addManualContactForLead,
  };
};
